import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CircularProgress from '@mui/material/CircularProgress';
import CustomizedButton from "../../components/CustomizedButton";
import { useEffect, useState, useRef } from "react";
import Graphs from "./Graphs";
import SummaryMetricsChart from "./SummaryMetricsChart";
import InfoGrid from "./InfoGrid";
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { styled } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { useDashboardContext } from "../../contexts/DashboardContext";
import OneTapButton from "./OneTapButton";
import { Button, Modal } from "@mui/material";
import { Modal as BaseModal } from '@mui/base/Modal';
import TextField from '@mui/material/TextField';
import CustomizeSelector from "./CustomizeSelector";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PDFDocument from "./PDFDocument";
import { PDFDownloadLink } from '@react-pdf/renderer';
import Chip from '@mui/material/Chip';
import { getTrialByTrialId, getSummaryDataByTrialId, analyzeData } from '../../utils/fastAPI'
import AIAnalysisBox from './AIAnalysisBox';
import html2canvas from 'html2canvas';

const AnalyzeTrial = () => {
  const { latestTrial, latestTrialId, patientName, patientInfo, allTrials } = useDashboardContext();
  const { trialId: trialIdFromUrl, patientId } = useParams();
  const navigate = useNavigate();
  const [currentTrialId, setCurrentTrialId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTrialInfo, setSelectedTrialInfo] = useState(null);
  const [selectedMetrics, setSelectedMetrics] = useState(["Gait Cycle", "Knee Flexion", "Hip Flexion", "Foot Drag Probability", "Ankle Speed", "Walking Probability"]);
  const [isAIAnalysis, setIsAIAnalysis] = useState(false);
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    if (!latestTrialId) {
      return;
    }
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const [pdfCharts, setPdfCharts] = useState([]);
  const [aiAnalysisResult, setAiAnalysisResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [trialInfoLoading, setTrialInfoLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState({});
  const [dataReady, setDataReady] = useState(false);
  const [rawData, setRawData] = useState([]);
  const graphsRef = useRef(null);

  useEffect(() => {
    let initialIdToLoad = trialIdFromUrl || latestTrialId;
    let dateToSetForPicker = null;
    let actualTrialIdToSet = null;

    if (initialIdToLoad && allTrials && allTrials.length > 0) {
      const foundTrial = allTrials.find(t => String(t.id) === String(initialIdToLoad));
      if (foundTrial) {
        actualTrialIdToSet = foundTrial.id;
        // Interpret the date string in the local timezone to avoid off-by-one errors
        dateToSetForPicker = foundTrial.date ? new Date(foundTrial.date + 'T00:00:00') : null;
      } else if (latestTrial && latestTrial.date) {
        actualTrialIdToSet = latestTrial.id;
        dateToSetForPicker = new Date(latestTrial.date + 'T00:00:00');
      }
    } else if (latestTrial && latestTrial.date) {
      actualTrialIdToSet = latestTrial.id;
      dateToSetForPicker = new Date(latestTrial.date + 'T00:00:00');
    }

    setCurrentTrialId(actualTrialIdToSet);
    setSelectedDate(dateToSetForPicker);
  }, [trialIdFromUrl, latestTrialId, allTrials, latestTrial]);

  useEffect(() => {
    if (!currentTrialId) {
      setSelectedTrialInfo(null);
      setSummaryData({});
      setDataReady(false);
      return;
    }

    setTrialInfoLoading(true);
    setDataReady(false);
    let active = true;

    const fetchTrialAndSummaryData = async () => {
      try {
        const [trialDataResponse, summaryDataResponse] = await Promise.all([
          getTrialByTrialId(currentTrialId),
          getSummaryDataByTrialId(currentTrialId)
        ]);

        if (active) {
          setSelectedTrialInfo(trialDataResponse);
          setSummaryData(summaryDataResponse);
          if (patientId && currentTrialId) {
            navigate(`/dashboard/${patientId}/analyze/${currentTrialId}`, { replace: true });
          }
          setTimeout(() => {
            if (active) {
              setDataReady(true);
            }
          }, 100);
        }
      } catch (err) {
        console.error("Error fetching trial or summary data:", err);
        if (active) {
          setError('Failed to load trial data.');
        }
      } finally {
        if (active) {
          setTrialInfoLoading(false);
        }
      }
    };

    fetchTrialAndSummaryData();

    return () => {
      active = false;
    };
  }, [currentTrialId, patientId, navigate]);

  const StyledTypography = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#5a5a5a',
    display: 'inline-block',
    marginRight: theme.spacing(2),
  }));

  const ModalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: "80%",
    bgcolor: 'background.paper',
    border: '1px solid #F5F5F5',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
  };

  const handleSetLatestTrial = () => {
    setCurrentTrialId(latestTrialId);
  }

  const handleSetAllMetrics = () => {
    setSelectedMetrics(["Gait Cycle", "Knee Flexion", "Hip Flexion", "Walking Probability", "Foot Drag Probability", "Ankle Speed"]);
  }

  const handleClearAllMetrics = () => {
    setSelectedMetrics([]);
  }

  const handleAIAnalysis = async () => {
    setIsAIAnalysis(true);
    setAiAnalysisResult("");
    setLoading(true);
    setError(null);
    try {
      let imageUrls = [];
      if (graphsRef.current) {
        const canvas = await html2canvas(graphsRef.current, {
          scale: 1, // Reduce scale to decrease image size
          useCORS: true,
        });
        // Use JPEG with 60% quality to reduce file size significantly
        const base64 = canvas.toDataURL('image/jpeg', 0.6);
        imageUrls.push(base64);
      }

      const formattedSummaryData = summaryData ? {
        "Steps": handleDecimals(summaryData.peak_step_count) + " steps",
        "Walking Time": handleDecimals(summaryData.walking_time) + " s",
        "Speed": handleDecimals(summaryData.speed) + " m/s",
        "Distance": handleDecimals(summaryData.distance) + " m",
        "Cadence": handleDecimals(summaryData.cadence) + " / min",
        "Stride Length": handleDecimals(summaryData.step_length) + " m",
        "Swing %": handleDecimals(summaryData.swingRatio) + " %",
        "Stance %": handleDecimals(summaryData.stanceRatio) + " %",
        "Knee Flex° Mid Stance": handleDecimals(summaryData.midstanceKnee) + " °",
        "Knee Flex° Initial Swing": handleDecimals(summaryData.initialswingKnee) + " °",
        "Knee Flex° Mid Swing": handleDecimals(summaryData.midswingKnee) + " °",
        "Total Knee ROM°": handleDecimals(summaryData.romKnee) + " °",
      } : {};

      const trialInfo = {
        "date": selectedTrialInfo?.date,
        "timePeriod": selectedTrialInfo?.timePeriod,
        "deviceLocation": summaryData?.deviceLocation,
        "sessionType": selectedTrialInfo?.sessionType,
        "notes": selectedTrialInfo?.notes || selectedTrialInfo?.clinician_notes,
      };

      const result = await analyzeData(
        trialInfo,
        formattedSummaryData,
        patientInfo.conditionDescription,
        imageUrls.length > 0 ? imageUrls : null
      );
      setAiAnalysisResult(result);
    } catch (err) {
      setError('Failed to analyze data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const generateDefaultReportName = (patientName) => {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    return `Report_${patientName}_${year}_${month}_${day}`;
  }

  const [reportName, setReportName] = useState(() => {
    return generateDefaultReportName(patientName);
  });
  const handleInputChange = (event) => {
    setReportName(event.target.value);
  };

  const [selectedReportSections, setSelectedReportSections] = useState(["Graph", "Summary Metrics"]);
  const namesForReportSections = [
    "Graph",
    "Summary Metrics",
  ];

  const namesForReportMetrics = [
    "Gait Cycle",
    "Walking Probability",
    "Knee Flexion",
    "Hip Flexion",
    "Foot Drag Probability",
    "Ankle Speed",
  ];

  const handleDecimals = (value) => {
    if (Number.isInteger(value)) {
      return value;
    } else {
      return parseFloat(value).toFixed(1);
    }
  };

  const handleReportTableContent = () => {
    if (summaryData) {
      return {
        "Steps": handleDecimals(summaryData.peak_step_count) + " steps",
        "Walking Time": handleDecimals(summaryData.walking_time) + " s",
        "Speed": handleDecimals(summaryData.speed) + " m/s",
        "Distance": handleDecimals(summaryData.distance) + " m",
        "Cadence": handleDecimals(summaryData.cadence) + " / min",
        "Stride Length": handleDecimals(summaryData.step_length) + " m",
        "Swing %": handleDecimals(summaryData.swingRatio) + " %",
        "Stance %": handleDecimals(summaryData.stanceRatio) + " %",
        "Knee Flex° Mid Stance": handleDecimals(summaryData.midstanceKnee) + " °",
        "Knee Flex° Initial Swing": handleDecimals(summaryData.initialswingKnee) + " °",
        "Knee Flex° Mid Swing": handleDecimals(summaryData.midswingKnee) + " °",
        "Total Knee ROM°": handleDecimals(summaryData.romKnee) + " °",
      };
    }
  }

  const handleExportToCSV = () => {
    if (!rawData || rawData.length === 0) {
      console.error("No data available to export");
      return;
    }

    // Prepare CSV headers
    const headers = [
      "Timestamp (s)",
      "Gait Cycle (%)",
      "Knee Flexion (°)",
      "Hip Flexion (°)",
      "Foot Drag Probability",
      "Ankle Speed (m/s)",
      "Walking Probability"
    ];

    // Calculate the minimum time for normalization
    const timeSeries = rawData.map(item => item.ms / 1000);
    const minTime = Math.min(...timeSeries);
    const normalizedTimeSeries = timeSeries.map(time => time - minTime);

    // Prepare CSV rows
    const csvRows = [headers.join(",")];
    
    rawData.forEach((item, index) => {
      const row = [
        normalizedTimeSeries[index].toFixed(3),
        item.gaitVals_percent !== undefined ? item.gaitVals_percent : "",
        item.knee_flex !== undefined ? item.knee_flex : "",
        item.hip_flex !== undefined ? item.hip_flex : "",
        item.footdrag !== undefined ? item.footdrag : "",
        item.ankle_speed_vx !== undefined ? item.ankle_speed_vx : "",
        item.isWalk !== undefined ? item.isWalk : ""
      ];
      csvRows.push(row.join(","));
    });

    // Create CSV content
    const csvContent = csvRows.join("\n");

    // Create a Blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    // Generate filename with patient name and trial info
    const date = selectedTrialInfo?.date || new Date().toISOString().split('T')[0];
    // Use patientName from context, with fallback to patientInfo if needed
    const safePatientName = patientName || patientInfo?.name || patientInfo?.firstName || 'Patient';
    const fileName = `EvoWalk_Recording_${safePatientName}_${currentTrialId}_${date}_raw_data.csv`
    
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <Box display="flex" flexDirection="column" m={1}>
      <Box display="flex">
        <OneTapButton title="New Trial Report" subtitle="Generate a gait report for this trial" onClick={handleOpen}/>
        <OneTapButton title="Analyze Trial" subtitle="Trial Analysis based on gait metrics" onClick={handleAIAnalysis} />
        <OneTapButton title="Select Latest Trial" subtitle="Set trial to latest trial" onClick={handleSetLatestTrial} />
      </Box>
      <Box display="flex">
        <Modal
          aria-labelledby="unstyled-modal-title"
          aria-describedby="unstyled-modal-description"
          open={open}
        >
          <Box sx={{ ...ModalStyle, position: 'relative' }} display="flex" flexDirection="column" gap={2}>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <StyledTypography variant="h7">choose sections:</StyledTypography>
            <Box bgcolor="#F5F5F5" borderRadius={1} width="100%">
              <CustomizeSelector selectedMetrics={selectedReportSections} onChange={setSelectedReportSections} names={namesForReportSections} />
            </Box>
            <StyledTypography variant="h7">choose metrics:</StyledTypography>
            <Box bgcolor="#F5F5F5" borderRadius={1} width="100%">
              <CustomizeSelector selectedMetrics={selectedMetrics} onChange={setSelectedMetrics} names={namesForReportMetrics} />
            </Box>
            <StyledTypography variant="h7">rename report:</StyledTypography>
            <Box bgcolor="#F5F5F5" borderRadius={1} width="100%">
              <TextField
                id="outlined-basic"
                value={reportName}
                onChange={handleInputChange}
                fullWidth
                borderRadius={2}
                aria-label="none"
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'transparent',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'transparent',
                    },
                    '& fieldset': { border: 'none' },
                  },
                }}
              />
            </Box>
            <Box display="flex" gap={2}>
              <Button variant="outlined">
                <PDFDownloadLink document={<PDFDocument trialInfo={selectedTrialInfo} tableContent={handleReportTableContent(summaryData)} summaryData={summaryData} patientInfo={patientInfo} pdfCharts={pdfCharts} />} fileName={`${reportName}.pdf`}>
                  {({ blob, url, loading, error }) => (loading ? 'Generating document...' : 'Download document')}
                </PDFDownloadLink>
              </Button>
              <Button variant="outlined" onClick={handleClose}>Cancel</Button>
            </Box>
          </Box>
        </Modal>
      </Box>
      <AIAnalysisBox 
        isAIAnalysis={isAIAnalysis}
        setIsAIAnalysis={setIsAIAnalysis}
        aiAnalysisResult={aiAnalysisResult}
        loading={loading}
        error={error}
      />
      <Box display="flex" mb={1}>
        <StyledTypography variant="h6" >Select Trial</StyledTypography>
        {currentTrialId === latestTrialId && <Chip label="LATEST TRIAL" sx={{ fontWeight: 'bold' }} />}
      </Box>
      <Box display="flex" flexDirection="column" mb={2} gap={2}>
        <InfoGrid
          selectedTrialInfo={selectedTrialInfo}
          setCurrentTrialId={setCurrentTrialId}
          currentTrialId={currentTrialId}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          loading={trialInfoLoading}
          summaryData={summaryData}
        />
        <SummaryMetricsChart summaryData={summaryData} loading={trialInfoLoading} />
      </Box>
      {!trialInfoLoading && dataReady && selectedTrialInfo &&
        selectedTrialInfo.sessionType &&
        selectedTrialInfo.sessionType.toLowerCase().includes('detailed') ? (
        <Box>
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
            <StyledTypography variant="h6">
            Select Metrics
          </StyledTypography>
          <Box display="flex" gap={2}>
            <Button
              onClick={handleExportToCSV}
              disabled={!rawData || rawData.length === 0}
              sx={{
                backgroundColor: '#F5F5F5',
                border: '2px solid #C0C0C0',
                color: '#000000',
                fontSize: '14px',
                transition: 'background-color 0.3s',
                '&:hover': {
                  backgroundColor: '#E0E0E0',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#F5F5F5',
                  border: '2px solid #E0E0E0',
                  color: '#A0A0A0',
                },
              }}
            >
              <FileDownloadIcon fontSize="small" />
              <span style={{ marginLeft: '5px' }}>EXPORT TO CSV</span>
            </Button>
            <Button
              onClick={handleSetAllMetrics}
              sx={{
                backgroundColor: '#F5F5F5',
                border: '2px solid #C0C0C0',
                color: '#000000',
                fontSize: '14px',
                transition: 'background-color 0.3s',
                '&:hover': {
                  backgroundColor: '#E0E0E0',
                },
              }}
            >
              <SelectAllIcon fontSize="small" />
              <span style={{ marginLeft: '5px' }}>SELECT ALL</span>
            </Button>
            <Button
              onClick={handleClearAllMetrics}
              sx={{
                backgroundColor: '#F5F5F5',
                border: '2px solid #C0C0C0',
                color: '#000000',
                fontSize: '14px',
                transition: 'background-color 0.3s',
                '&:hover': {
                  backgroundColor: '#E0E0E0',
                },
              }}
            >
              <ClearAllIcon fontSize="small" />
              <span style={{ marginLeft: '5px' }}>CLEAR ALL</span>
            </Button>
          </Box>
        </Box>
        <div ref={graphsRef}>
          <Graphs 
            currentTrialId={currentTrialId} 
            selectedMetrics={selectedMetrics} 
            setSelectedMetrics={setSelectedMetrics} 
            setPdfCharts={setPdfCharts} 
            setRawData={setRawData}
          />
          </div>
        </Box>
      ) : trialInfoLoading || !dataReady ? (
        <Box>
          <Box display="flex" flexDirection="column" width="100%" mb={2}>
            <Box display="flex" justifyContent="center" p={3} bgcolor="#f5f5f5" borderRadius={1}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography color="text.secondary">Loading trial data...</Typography>
            </Box>
          </Box>
        </Box>
      ) : selectedTrialInfo &&
        selectedTrialInfo.sessionType &&
        !selectedTrialInfo.sessionType.toLowerCase().includes('detailed') ? (
        <Box>
          <Box display="flex" flexDirection="column" width="100%" mb={2}>
            <Box display="flex" justifyContent="center" p={3} bgcolor="#f5f5f5" borderRadius={1}>
              <Typography color="text.secondary">Please select a detailed data collection if you want to plot the gait graphs</Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box>
          <Box display="flex" flexDirection="column" width="100%" mb={2}>
            <Box display="flex" justifyContent="center" p={3} bgcolor="#f5f5f5" borderRadius={1}>
              <Typography color="text.secondary">No trial data available</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AnalyzeTrial;
